import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { SuccessNotify, ErrorNotify } from "../../shared/Alert";
import { getCompanyDetails } from "../../helpers/backend_helper";
import { showLoading } from "react-global-loading";
import { postData } from "../../helpers/url-request";
import "../Standards/Standards.scss";

const CompanyDetail = ({ companyId, closeAction }) => {
  const [formData, setFormData] = useState({
    id: 0,
    name: "",
    address: "",
    contactNo: "",
    emailId: "",
    website: "",
  });
  const isEdit = Boolean(companyId);

  useEffect(() => {
    if (!companyId) return;
    showLoading(true);
    getCompanyDetails(companyId)
      .then((response) => {
        if (response.status) {
          const data = response.data;
          setFormData({
            id: data.id,
            name: data.name || "",
            address: data.address || "",
            contactNo: data.contactNo || "",
            emailId: data.emailId || "",
            website: data.website || "",
          });
        } else {
          ErrorNotify(response.message);
        }
      })
      .catch((error) => {
        console.error(error);
        ErrorNotify("Unable to load the firm details.");
      })
      .finally(() => showLoading(false));
  }, [companyId]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: formData,
    validationSchema: Yup.object({
      name: Yup.string().trim().required("Enter a firm name"),
      emailId: Yup.string().email("Enter a valid email address").nullable(),
      website: Yup.string().url("Enter a valid website URL").nullable(),
    }),
    onSubmit: (values) => {
      const request = {
        id: values.id || 0,
        name: values.name.trim(),
        address: values.address?.trim() || "",
        contactNo: values.contactNo?.trim() || "",
        emailId: values.emailId?.trim() || "",
        website: values.website?.trim() || "",
      };
      showLoading(true);
      postData("/company/addupdate", request)
        .then((response) => {
          if (response.status) {
            SuccessNotify(
              isEdit
                ? "Firm updated successfully."
                : "Firm created successfully."
            );
            closeAction?.();
          } else {
            ErrorNotify(response.message);
          }
        })
        .catch((error) => {
          console.error(error);
          ErrorNotify("Unable to save the firm.");
        })
        .finally(() => showLoading(false));
    },
  });

  return (
    <Form
      className="standard-drawer-form"
      onSubmit={(event) => {
        event.preventDefault();
        validation.handleSubmit();
      }}
    >
      <div className="standard-drawer-form__content">
        <p className="standard-drawer-form__intro">
          {isEdit
            ? "Update this firm's profile and contact information."
            : "Add a firm to make it available across the platform."}
        </p>
        <section className="standard-drawer-form__section">
          <div className="standard-drawer-form__section-title">
            <span>
              <i className="bx bx-building-house" />
              Firm Information
            </span>
            <i className="bx bx-chevron-down" />
          </div>
          <FormGroup>
            <Label htmlFor="firm-name">
              Firm Name <b>*</b>
            </Label>
            <Input
              id="firm-name"
              name="name"
              placeholder="Enter firm name"
              value={validation.values.name}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={Boolean(
                validation.touched.name && validation.errors.name
              )}
            />
            {validation.touched.name && validation.errors.name && (
              <FormFeedback>{validation.errors.name}</FormFeedback>
            )}
          </FormGroup>
          <FormGroup>
            <Label htmlFor="firm-address">Address</Label>
            <Input
              id="firm-address"
              name="address"
              type="textarea"
              rows="4"
              maxLength="500"
              placeholder="Enter the firm address"
              value={validation.values.address}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
            />
            <div className="standard-drawer-form__count">
              {validation.values.address.length} / 500
            </div>
          </FormGroup>
        </section>
        <section className="standard-drawer-form__section">
          <div className="standard-drawer-form__section-title">
            <span>
              <i className="bx bx-phone" />
              Contact Details
            </span>
            <i className="bx bx-chevron-down" />
          </div>
          <FormGroup>
            <Label htmlFor="firm-contact">Contact Number</Label>
            <Input
              id="firm-contact"
              name="contactNo"
              placeholder="Enter contact number"
              value={validation.values.contactNo}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="firm-email">Email Address</Label>
            <Input
              id="firm-email"
              name="emailId"
              type="email"
              placeholder="Enter email address"
              value={validation.values.emailId}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={Boolean(
                validation.touched.emailId && validation.errors.emailId
              )}
            />
            {validation.touched.emailId && validation.errors.emailId && (
              <FormFeedback>{validation.errors.emailId}</FormFeedback>
            )}
          </FormGroup>
          <FormGroup>
            <Label htmlFor="firm-website">Website</Label>
            <Input
              id="firm-website"
              name="website"
              placeholder="https://example.com"
              value={validation.values.website}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={Boolean(
                validation.touched.website && validation.errors.website
              )}
            />
            {validation.touched.website && validation.errors.website && (
              <FormFeedback>{validation.errors.website}</FormFeedback>
            )}
          </FormGroup>
        </section>
        <section className="standard-drawer-form__section standard-drawer-form__section--info">
          <div className="standard-drawer-form__section-title">
            <span>
              <i className="bx bx-info-circle" />
              What happens next
            </span>
          </div>
          <div className="standard-drawer-form__next-steps">
            <strong>After saving, you can</strong>
            <span>
              <i className="bx bx-check" />
              Manage users and locations for this firm
            </span>
            <span>
              <i className="bx bx-check" />
              Use it in engagements and inspection workflows
            </span>
            <span>
              <i className="bx bx-check" />
              Update its contact details at any time
            </span>
          </div>
        </section>
      </div>
      <footer className="standard-drawer-form__actions">
        <Button
          className="standard-drawer-form__cancel"
          type="button"
          onClick={closeAction}
        >
          Cancel
        </Button>
        <Button className="standard-drawer-form__submit" type="submit">
          {isEdit ? "Save Changes" : "Create Firm"}
        </Button>
      </footer>
    </Form>
  );
};

export default CompanyDetail;
